import { DeleteOutlined, EditOutlined, FolderOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Modal,
  Pagination,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import CustomButton from 'components/custom-button';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import { shortenName } from 'utils/short-name';
import PageTitle from '../../components/page-title';
import ModalCreateEdit from './component/ModalCreateEdit';
import ModalMenuGroups from './component/ModalMenuGroups';
import {
  LINK_PROTECTION,
  LINK_PROTECTION_OPTIONS,
  MENU_TYPE_LABEL,
  MenuItem,
  getErrorMessage,
  isDirectFileLink,
  isInternalMenuLink,
  linkProtectionFields,
  linkProtectionOf,
} from './constants';
import styles from './styles.module.scss';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { TYPE_MENU } from 'constants/common';
import { renderHeaderTitle } from '../../components/ATableCell';
import { useCurrentRoles } from 'hooks/use-current-roles';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT, sortType: 'ASC' };

export interface Filter {
  limit: number;
  page: number;
  sortType: any;
}

const MenuManager = () => {
  // Sửa ứng dụng là việc của ADMIN (server trả 401 cho MOD → admin tự đăng
  // xuất). MOD vẫn xem được trang nhưng mọi nút sửa bị tắt.
  const { isAdmin, loaded: rolesLoaded } = useCurrentRoles();
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as Filter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);
  const [isOpenModalGroups, setIsOpenModalGroups] = useState(false);
  // Id các ứng dụng đang chờ server trả lời khi bật/tắt "Trang chủ".
  const [pinningIds, setPinningIds] = useState<number[]>([]);
  // Id các ứng dụng đang chờ server trả lời khi đổi mức bảo vệ link.
  const [protectingIds, setProtectingIds] = useState<number[]>([]);

  const onFinish = (value: Partial<Filter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    // Modal nhóm có thể đòi tải lại nhiều lần liên tiếp: chỉ nhận kết quả của lần gọi mới nhất.
    let ignore = false;
    const fetchMenus = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.menus, {
          ...filter,
        });
        if (!ignore) {
          setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchMenus();
    return () => {
      ignore = true;
    };
  }, [filter, statusUpdateData]);

  const onSortChange = async () => {
    setFilter((prevState) => {
      return {
        ...prevState,
        sortType: filter.sortType === 'DESC' ? 'ASC' : 'DESC',
      };
    });
  };

  const patchLocally = (id: number, patch: Partial<MenuItem>) => {
    setDataResponse((prev: any) => ({
      ...prev,
      items: (prev?.items || []).map((item: MenuItem) =>
        item.id === id ? { ...item, ...patch } : item
      ),
    }));
  };

  const setPinnedLocally = (id: number, isPinned: boolean) => patchLocally(id, { isPinned });

  const handleChangeProtection = async (record: MenuItem, value: LINK_PROTECTION) => {
    if (value === LINK_PROTECTION.VIEW_ONLY && isDirectFileLink(record.link)) {
      notification.warning({
        message: 'Không đặt được “Chỉ xem”',
        description: `Link của “${record.name}” là file tải về (PDF, Word, Excel...) nên app không khoá được. Hãy đổi sang link xem trực tuyến (bấm Sửa) rồi chọn lại.`,
        duration: 6,
      });
      return;
    }
    const previous = linkProtectionFields(linkProtectionOf(record));
    const next = linkProtectionFields(value);
    setProtectingIds((prev) => [...prev, record.id]);
    // Đổi ngay trên bảng; server báo lỗi thì trả lại như cũ.
    patchLocally(record.id, next);
    try {
      await AxiosUtils.patch(`${API.updateMenu}/${record.id}`, next);
    } catch (error) {
      patchLocally(record.id, previous);
      notification.error({
        message: 'Lỗi',
        description: getErrorMessage(error, `Không đổi được mức bảo vệ link của “${record.name}”.`),
        duration: 4,
      });
    } finally {
      setProtectingIds((prev) => prev.filter((id) => id !== record.id));
    }
  };

  const handleTogglePinned = async (record: MenuItem, checked: boolean) => {
    const previous = !!record.isPinned;
    setPinningIds((prev) => [...prev, record.id]);
    // Đổi ngay trên bảng; server báo lỗi thì trả lại như cũ.
    setPinnedLocally(record.id, checked);
    try {
      await AxiosUtils.patch(`${API.updateMenu}/${record.id}`, { isPinned: checked });
    } catch (error) {
      setPinnedLocally(record.id, previous);
      notification.error({
        message: 'Lỗi',
        description: getErrorMessage(
          error,
          checked
            ? `Không ghim được “${record.name}” lên Trang chủ.`
            : `Không bỏ ghim được “${record.name}”.`
        ),
        duration: 4,
      });
    } finally {
      setPinningIds((prev) => prev.filter((id) => id !== record.id));
    }
  };

  const columns: ColumnsType<MenuItem> = [
    // Bảng 11 cột không vừa khung 1118px (màn 1440) / 958px (màn 1280) nên cuộn ngang. Mọi cột
    // đều có độ rộng (trước đây 4 cột không đặt chỉ còn ~83px, chữ và ngày bị gãy giữa từ);
    // ID + Tên ghim bên trái, Thao tác ghim bên phải để lúc cuộn vẫn biết đang ở dòng nào và
    // luôn thấy nút Sửa.
    {
      title: 'ID',
      dataIndex: 'id',
      width: 64,
      fixed: 'left',
      ellipsis: true,
      key: 'id',
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 70) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: renderHeaderTitle('STT', filter.sortType, () => onSortChange()),
      dataIndex: 'position',
      key: 'position',
      width: 84,
      render: (value) => <span>{value}</span>,
    },
    {
      title: 'Nhóm',
      key: 'group',
      width: 160,
      render: (_, record) =>
        record.group?.name ? (
          <span>{record.group.name}</span>
        ) : (
          <Typography.Text type="secondary">Khác</Typography.Text>
        ),
    },
    {
      title: 'Trang chủ',
      dataIndex: 'isPinned',
      key: 'isPinned',
      width: 110,
      render: (value, record) => {
        const pending = pinningIds.includes(record.id);
        return (
          <Switch
            checked={!!value}
            loading={pending}
            disabled={pending || !isAdmin}
            onChange={(checked) => handleTogglePinned(record, checked)}
            aria-label={`Ghim “${record.name}” lên Trang chủ`}
          />
        );
      },
    },
    {
      title: 'Bảo vệ link',
      key: 'linkProtection',
      width: 204,
      render: (_, record) => {
        if (isInternalMenuLink(record.link)) {
          return (
            <Tooltip title="Mở màn có sẵn trong app, không phải trang web">
              <Typography.Text type="secondary">Màn trong app</Typography.Text>
            </Tooltip>
          );
        }
        const pending = protectingIds.includes(record.id);
        const value = linkProtectionOf(record);
        return (
          <Select
            size="small"
            value={value}
            loading={pending}
            disabled={pending || !isAdmin}
            style={{ width: 170 }}
            className={value === LINK_PROTECTION.VIEW_ONLY ? styles.protectionLocked : undefined}
            popupMatchSelectWidth={false}
            // Không đặt getPopupContainer: bảng cuộn ngang cắt mất danh sách
            // khi trang ít dòng. Mặc định gắn vào body.
            onChange={(next: LINK_PROTECTION) => handleChangeProtection(record, next)}
            aria-label={`Bảo vệ link của “${record.name}”`}
            options={LINK_PROTECTION_OPTIONS.map((option) => ({
              value: option.value,
              label: option.short,
              hint: option.hint,
            }))}
            // Giải thích chỉ hiện khi rê chuột trong danh sách chọn, không hiện
            // trên ô đã chọn (đè lên cột bên cạnh).
            optionRender={(option) => (
              <Tooltip title={option.data.hint} placement="left">
                <div>{option.label}</div>
              </Tooltip>
            )}
          />
        );
      },
    },
    {
      title: 'Ai thấy',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (value: TYPE_MENU) => MENU_TYPE_LABEL[value] || value,
    },
    {
      title: 'Liên kết',
      dataIndex: 'link',
      key: 'link',
      width: 180,
      // Một dòng, cắt "…" (link dài và mã như APPROVED_OVERTIME không còn bị bẻ 3–4 dòng);
      // rê chuột xem đủ.
      ellipsis: { showTitle: false },
      render: (value) =>
        !value ? null : isInternalMenuLink(value) ? (
          <Tooltip title="Mở màn có sẵn trong app">
            <Typography.Text type="secondary">{value}</Typography.Text>
          </Tooltip>
        ) : (
          <Tooltip title={value}>
            <a href={`${value}`} target="_blank" rel="noreferrer">
              {value}
            </a>
          </Tooltip>
        ),
    },
    {
      title: 'Biểu tượng',
      dataIndex: 'icon',
      key: 'icon',
      width: 112,
      render: (value) =>
        value ? (
          <div className={styles.iconBox}>
            <img src={`${SERVER_FILE_ENDPOINT}/${value}`} alt="" />
          </div>
        ) : null,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (value) => <span className={styles.nowrap}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 100,
      fixed: 'right',
      key: 'action',
      render: (_, record) =>
        !isAdmin ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label={`Sửa “${record.name}”`}
              onClick={() => handelOpenCreateEditModal(record)}
            />
          </Tooltip>
        ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData((prev) => !prev);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteMenu, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      setSelectedRowKeys([]);
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: getErrorMessage(error, 'Không xóa được ứng dụng.'),
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Quản lý ứng dụng"
        extra={
          isAdmin && (
            <Space wrap>
              {hasSelected && (
                <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                  Xóa {selectedRowKeys.length} ứng dụng
                </Button>
              )}
              <CustomButton
                onClick={() => setIsOpenModalGroups(true)}
                bgColor="secondary-green"
                text="Quản lý nhóm"
                icon={<FolderOutlined />}
              />
              <CustomButton
                onClick={() => handelOpenCreateEditModal(null)}
                bgColor="primary-green"
                text="Thêm ứng dụng"
                icon={<PlusOutlined />}
              />
            </Space>
          )
        }
      />
      {rolesLoaded && !isAdmin && (
        <Alert
          type="warning"
          showIcon
          className={styles.guide}
          message="Tài khoản MOD chỉ xem được trang này. Thêm, sửa, xoá ứng dụng cần tài khoản ADMIN."
        />
      )}
      <Alert
        type="info"
        showIcon
        className={styles.guide}
        message={
          <ul className={styles.guideList}>
            <li>
              Trang chủ app mới có 8 ô: 7 ứng dụng bật “Trang chủ” (theo số thứ tự) và ô “Tất cả”
              (nếu mọi ứng dụng người đó thấy vừa đủ 8 ô thì hiện cả 8, không cần ô “Tất cả”). Nếu
              không bật ứng dụng nào, app lấy các ứng dụng đầu tiên.
            </li>
            <li>Màn “Tất cả ứng dụng” chia theo Nhóm; ứng dụng không có nhóm nằm ở mục “Khác”.</li>
            <li>Cột “Ai thấy” quyết định ai nhìn thấy ứng dụng.</li>
            <li>
              Cột “Bảo vệ link” (app mới): <b>Chỉ xem</b> = khoá hẳn trang, không sao chép / dán /
              bấm được gì, chỉ cuộn xem và quay lại; <b>Ẩn link</b> = không lấy được link nhưng vẫn
              điền được (dùng cho Google Form); các mức “Cho…” hiện nút ⓘ góc phải. App cũ chưa cập
              nhật: mục “Chỉ xem” hiện trang “Cần cập nhật ứng dụng” thay cho nội dung.
            </li>
          </ul>
        }
      />
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          // Tổng độ rộng các cột (kể cả ô chọn ~50px). Không dùng 'max-content': link dài
          // không xuống dòng sẽ kéo giãn cột Liên kết.
          scroll={{ x: 1500 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      {isOpenModalGroups && (
        <ModalMenuGroups
          isOpen={isOpenModalGroups}
          onClose={() => setIsOpenModalGroups(false)}
          onChanged={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa ứng dụng"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} ứng dụng?</p>
      </Modal>
    </div>
  );
};

export default MenuManager;
